import { Suspense, useState } from 'react';
import SEOHead from '@/components/feature/SEOHead';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

const insightsJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Blog',
  name: '픽스코프 인사이트 | Fixcope Insights',
  url: `${siteUrl}/insights`,
  description: '기술 트렌드, 산업 인사이트, 혁신 솔루션에 관한 픽스코프의 전문 아티클.',
  inLanguage: 'ko-KR',
  publisher: {
    '@type': 'Organization',
    name: 'Fixcope',
    url: siteUrl,
  },
};

export default function InsightsPage() {
  const [selectedCategory, setSelectedCategory] = useState('All');

  const categories = ['All', 'Technology', 'Business', 'Innovation', 'Security', 'Cloud'];

  const articles = [
    {
      category: 'Technology',
      title: 'The Future of AI in Enterprise Solutions',
      excerpt: 'Exploring how artificial intelligence is revolutionizing business operations and decision-making processes across industries.',
      image: 'https://public.readdy.ai/ai/img_res/56260e339e2b6f1ddf802a9a9114d8a9.jpg',
      author: 'Sarah Johnson',
      date: 'March 15, 2024',
      readTime: '8 min read',
      featured: true
    },
    {
      category: 'Security',
      title: 'Cybersecurity Best Practices for 2024',
      excerpt: 'Essential security strategies and frameworks to protect your organization from evolving cyber threats.',
      image: 'https://readdy.ai/api/search-image?query=Advanced%20cybersecurity%20operations%20center%20with%20multiple%20monitoring%20screens%20displaying%20network%20security%20data%2C%20digital%20shield%20icons%2C%20encryption%20symbols%2C%20security%20analysts%20working%20with%20threat%20detection%20systems%2C%20dark%20blue%20ambient%20lighting%2C%20firewall%20visualization%2C%20secure%20network%20infrastructure%20with%20glowing%20security%20protocols%20and%20cyber%20defense%20technology&width=800&height=600&seq=insight-cybersecurity-001&orientation=landscape',
      author: 'Michael Chen',
      date: 'March 12, 2024',
      readTime: '6 min read',
      featured: true
    },
    {
      category: 'Cloud',
      title: 'Cloud Migration Strategies That Work',
      excerpt: 'A comprehensive guide to successful cloud adoption, from planning to execution and optimization.',
      image: 'https://readdy.ai/api/search-image?query=Modern%20cloud%20computing%20infrastructure%20visualization%20with%20server%20racks%2C%20data%20center%20environment%2C%20cloud%20network%20connections%2C%20digital%20cloud%20icons%20floating%20above%20servers%2C%20blue%20ambient%20lighting%2C%20distributed%20computing%20systems%2C%20virtualization%20technology%2C%20scalable%20cloud%20architecture%20with%20glowing%20data%20pathways%20and%20network%20connectivity&width=800&height=600&seq=insight-cloud-migration-001&orientation=landscape',
      author: 'Emily Rodriguez',
      date: 'March 10, 2024',
      readTime: '10 min read',
      featured: true
    },
    {
      category: 'Business',
      title: 'Digital Transformation: A Leadership Perspective',
      excerpt: 'How C-level executives can drive successful digital transformation initiatives in their organizations.',
      image: 'https://readdy.ai/api/search-image?query=Executive%20business%20meeting%20with%20digital%20transformation%20concepts%2C%20modern%20boardroom%20with%20large%20displays%20showing%20business%20analytics%2C%20professional%20leaders%20discussing%20strategy%2C%20contemporary%20office%20environment%20with%20blue%20accent%20lighting%2C%20digital%20innovation%20visualization%2C%20business%20intelligence%20dashboards%2C%20corporate%20leadership%20in%20technology-driven%20workspace&width=800&height=600&seq=insight-digital-transformation-001&orientation=landscape',
      author: 'David Thompson',
      date: 'March 8, 2024',
      readTime: '7 min read',
      featured: false
    },
    {
      category: 'Innovation',
      title: 'IoT and Edge Computing: The Next Frontier',
      excerpt: 'Understanding the convergence of IoT and edge computing and its impact on real-time data processing.',
      image: 'https://readdy.ai/api/search-image?query=Internet%20of%20Things%20technology%20environment%20with%20connected%20devices%2C%20IoT%20sensors%20network%2C%20edge%20computing%20infrastructure%2C%20smart%20devices%20communicating%20wirelessly%2C%20modern%20tech%20facility%20with%20blue%20ambient%20lighting%2C%20real-time%20data%20processing%20visualization%2C%20distributed%20computing%20nodes%2C%20connected%20ecosystem%20with%20glowing%20network%20connections%20and%20data%20flow&width=800&height=600&seq=insight-iot-edge-001&orientation=landscape',
      author: 'Lisa Wang',
      date: 'March 5, 2024',
      readTime: '9 min read',
      featured: false
    },
    {
      category: 'Technology',
      title: 'Blockchain Beyond Cryptocurrency',
      excerpt: 'Exploring practical enterprise applications of blockchain technology in supply chain, healthcare, and finance.',
      image: 'https://readdy.ai/api/search-image?query=Blockchain%20technology%20visualization%20with%20distributed%20ledger%20network%2C%20digital%20blocks%20connected%20in%20chain%2C%20cryptographic%20security%20symbols%2C%20decentralized%20network%20nodes%2C%20modern%20tech%20environment%20with%20blue%20ambient%20lighting%2C%20smart%20contracts%20visualization%2C%20transparent%20data%20blocks%2C%20enterprise%20blockchain%20infrastructure%20with%20glowing%20connections%20and%20secure%20data%20flow&width=800&height=600&seq=insight-blockchain-enterprise-001&orientation=landscape',
      author: 'James Martinez',
      date: 'March 3, 2024',
      readTime: '8 min read',
      featured: false
    },
    {
      category: 'Security',
      title: 'Zero Trust Architecture Implementation',
      excerpt: 'A practical guide to implementing zero trust security models in modern enterprise environments.',
      image: 'https://readdy.ai/api/search-image?query=Zero%20trust%20security%20architecture%20visualization%20with%20identity%20verification%20systems%2C%20multi-factor%20authentication%20displays%2C%20network%20segmentation%20diagrams%2C%20security%20checkpoints%2C%20modern%20cybersecurity%20operations%20center%20with%20blue%20ambient%20lighting%2C%20access%20control%20systems%2C%20encrypted%20data%20pathways%2C%20comprehensive%20security%20framework%20with%20layered%20protection%20and%20identity%20management&width=800&height=600&seq=insight-zero-trust-001&orientation=landscape',
      author: 'Rachel Kim',
      date: 'March 1, 2024',
      readTime: '7 min read',
      featured: false
    },
    {
      category: 'Cloud',
      title: 'Multi-Cloud Strategy: Benefits and Challenges',
      excerpt: 'Navigating the complexities of multi-cloud environments and maximizing their strategic advantages.',
      image: 'https://readdy.ai/api/search-image?query=Multi-cloud%20infrastructure%20visualization%20with%20multiple%20cloud%20platforms%20connected%2C%20hybrid%20cloud%20architecture%2C%20distributed%20data%20centers%2C%20cloud%20service%20integration%2C%20modern%20tech%20environment%20with%20blue%20ambient%20lighting%2C%20cloud%20orchestration%20systems%2C%20seamless%20cloud%20connectivity%2C%20enterprise%20multi-cloud%20strategy%20with%20interconnected%20cloud%20services%20and%20unified%20management&width=800&height=600&seq=insight-multicloud-001&orientation=landscape',
      author: 'Alex Turner',
      date: 'February 28, 2024',
      readTime: '6 min read',
      featured: false
    },
    {
      category: 'Innovation',
      title: 'Quantum Computing: Preparing for the Future',
      excerpt: 'Understanding quantum computing fundamentals and preparing your organization for the quantum era.',
      image: 'https://readdy.ai/api/search-image?query=Quantum%20computing%20technology%20visualization%20with%20quantum%20processors%2C%20superconducting%20qubits%2C%20cryogenic%20cooling%20systems%2C%20quantum%20circuit%20diagrams%2C%20advanced%20research%20laboratory%20with%20blue%20ambient%20lighting%2C%20quantum%20entanglement%20visualization%2C%20futuristic%20computing%20infrastructure%2C%20quantum%20algorithms%20and%20superposition%20states%20with%20glowing%20quantum%20particles&width=800&height=600&seq=insight-quantum-computing-001&orientation=landscape',
      author: 'Dr. Patricia Lee',
      date: 'February 25, 2024',
      readTime: '10 min read',
      featured: false
    }
  ];

  const filteredArticles = selectedCategory === 'All' 
    ? articles 
    : articles.filter(article => article.category === selectedCategory);

  const featuredArticles = articles.filter(article => article.featured);

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <div className="min-h-screen bg-white">
        <SEOHead
          title="픽스코프 인사이트 | Fixcope - 기술 트렌드·산업 분석·혁신 아티클"
          description="기술 트렌드, 산업 인사이트, 혁신 솔루션에 관한 픽스코프의 전문 아티클을 확인하세요. AI, 클라우드, 보안, 디지털 전환 최신 동향."
          keywords="픽스코프 인사이트, 기술 트렌드, AI, 클라우드, 사이버보안, 디지털 전환, Fixcope insights"
          canonical="/insights"
          ogTitle="픽스코프 인사이트 | Fixcope"
          ogDescription="기술 트렌드와 산업 인사이트를 담은 픽스코프의 전문 아티클."
          lang="ko"
          jsonLd={insightsJsonLd}
        />
        <Navbar />
        
        {/* Hero Section */}
        <section className="pt-32 pb-20 px-8 bg-gradient-to-br from-blue-50 to-white">
          <div className="max-w-7xl mx-auto text-center">
            <h1 className="text-6xl font-bold text-[#1A2332] mb-6">
              Insights & Perspectives
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Stay ahead with expert insights on technology trends, industry best practices, and innovative solutions shaping the future of business.
            </p>
          </div>
        </section>

        {/* Featured Articles */}
        <section className="py-20 px-8 bg-white">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-4xl font-bold text-[#1A2332] mb-12">
              Featured Articles
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {featuredArticles.map((article) => (
                <div
                  key={article.title}
                  className="group cursor-pointer"
                >
                  <div className="relative rounded-2xl overflow-hidden mb-6 h-[280px]">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                    <div className="absolute top-4 left-4">
                      <span className="px-4 py-1.5 bg-blue-600 text-white text-sm font-semibold rounded-full whitespace-nowrap">
                        {article.category}
                      </span>
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold text-[#1A2332] mb-3 group-hover:text-blue-600 transition-colors">
                    {article.title}
                  </h3>
                  <p className="text-gray-600 mb-4 leading-relaxed">
                    {article.excerpt}
                  </p>
                  <div className="flex items-center justify-between text-sm text-gray-500">
                    <div className="flex items-center space-x-4">
                      <span>{article.author}</span>
                      <span>•</span>
                      <span>{article.date}</span>
                    </div>
                    <span>{article.readTime}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Category Filter */}
        <section className="py-12 px-8 bg-gradient-to-br from-blue-50 to-white">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-wrap gap-3 justify-center">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-all whitespace-nowrap ${
                    selectedCategory === category
                      ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* All Articles */}
        <section className="py-20 px-8 bg-white">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-4xl font-bold text-[#1A2332] mb-12">
              {selectedCategory === 'All' ? 'All Articles' : `${selectedCategory} Articles`}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredArticles.map((article) => (
                <div
                  key={article.title}
                  className="group cursor-pointer bg-gradient-to-br from-blue-50 to-white rounded-2xl overflow-hidden hover:shadow-xl transition-all"
                >
                  <div className="relative h-[240px] overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
                    <div className="absolute top-4 left-4">
                      <span className="px-3 py-1 bg-blue-600 text-white text-xs font-semibold rounded-full whitespace-nowrap">
                        {article.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-[#1A2332] mb-3 group-hover:text-blue-600 transition-colors">
                      {article.title}
                    </h3>
                    <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span>{article.author}</span>
                      <span>{article.readTime}</span>
                    </div>
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <span className="text-xs text-gray-500">{article.date}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Newsletter Section */}
        <section className="py-20 px-8 bg-gradient-to-br from-blue-50 to-white">
          <div className="max-w-4xl mx-auto text-center">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center mx-auto mb-6">
              <i className="ri-mail-line text-3xl text-white"></i>
            </div>
            <h2 className="text-5xl font-bold text-[#1A2332] mb-6">
              Stay Informed
            </h2>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Subscribe to our newsletter and get the latest insights delivered directly to your inbox.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 max-w-xl mx-auto">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-6 py-4 rounded-lg border border-gray-300 focus:outline-none focus:border-blue-500 text-sm"
              />
              <button className="px-8 py-4 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg font-semibold hover:from-blue-600 hover:to-blue-700 transition-all whitespace-nowrap">
                Subscribe
              </button>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </Suspense>
  );
}
